/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  Dropdown,
  DropdownList,
  DropdownUserAssistance,
  EmptyResults,
  LinkItem,
  preventDefault
} from '#PRIVATE_SelectCommon';
import type { DataState } from '#UNSAFE_Collection';
import type { PositionData } from '#UNSAFE_Floating';
import { Separator } from '#UNSAFE_Separator';
import { InlineUserAssistance, type UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import type { ComponentChildren, ComponentProps, RefObject } from 'preact';
import type { CollectionRendererProps } from './SelectMultiple.types';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props<K extends string | number, D extends Record<string, any>> = {
  assistiveText?: string;
  collectionRenderer: (props: CollectionRendererProps<K, D>) => ComponentChildren;
  collectionRendererContext: CollectionRendererProps<K, D>;
  data?: DataState<K, D> | null;
  dropdownId: string;
  dropdownRef: RefObject<HTMLDivElement>;
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  isAddToListShown?: boolean;
  isDisabled?: boolean;
  isDropdownAbove: boolean;
  isEmptyResults: boolean;
  isOpen: boolean;
  isReadonly?: false;
  label: string;
  mainFieldRef: RefObject<HTMLDivElement>;
  userAssistanceDensity?: UserAssistanceDensityType;
  onAddToListAction?: () => void;
  onAutoDismiss: ((event?: Event | undefined) => void) | ((event?: Event | undefined) => void);
  onPosition: (data: PositionData) => void;
};

export function SelectMultipleDesktopDropdown<
  K extends string | number,
  D extends Record<string, any>
>({
  assistiveText,
  collectionRenderer,
  collectionRendererContext,
  data,
  dropdownId,
  dropdownRef,
  helpSourceLink,
  helpSourceText,
  isAddToListShown,
  isDisabled,
  isDropdownAbove,
  isEmptyResults,
  isOpen,
  isReadonly,
  label,
  mainFieldRef,
  userAssistanceDensity,
  onAddToListAction,
  onAutoDismiss,
  onPosition
}: Props<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const dropdownInlineUserAssistance =
    isOpen &&
    !isDropdownAbove &&
    !isDisabled &&
    !isReadonly &&
    (assistiveText || helpSourceLink) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <TabbableModeContext.Provider value={{ isTabbable: false }}>
        <DropdownUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          userAssistanceDensity={userAssistanceDensity}
        />
      </TabbableModeContext.Provider>
    ) : undefined;

  const dropdownListContent = isEmptyResults ? (
    isAddToListShown ? undefined : (
      <EmptyResults />
    )
  ) : (
    collectionRenderer(collectionRendererContext)
  );

  const addToListContent = isAddToListShown ? (
    <>
      {!isEmptyResults && (
        <div onMouseDown={preventDefault}>
          <Separator />
        </div>
      )}
      <LinkItem isHighlighted={isEmptyResults} onAction={onAddToListAction}>
        {translations.select_addToList()}
      </LinkItem>
    </>
  ) : undefined;

  return (
    <Dropdown
      dropdownRef={dropdownRef}
      id={dropdownId}
      isOpen={isOpen}
      anchorRef={mainFieldRef}
      onAutoDismiss={onAutoDismiss}
      onPosition={onPosition}>
      <DropdownList
        hasBottomGap={dropdownInlineUserAssistance === undefined}
        isLoading={data === null}>
        {dropdownListContent}
        {addToListContent}
      </DropdownList>
      {dropdownInlineUserAssistance}
    </Dropdown>
  );
}
